import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminService } from '@/services/adminService';
import type { UserProfile, UserInvite, Tenant, UserRole, EmailConfig, VapiConfig, IntegrationProvider, Site, Customer } from '@/types/models';

// ── Users ──────────────────────────────────────────────

export function useUsers() {
  return useQuery({ queryKey: ['admin', 'users'], queryFn: () => adminService.getUsers() });
}

export function useUser(id: string) {
  return useQuery({ queryKey: ['admin', 'users', id], queryFn: () => adminService.getUser(id), enabled: !!id });
}

export function useUpdateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<UserProfile> }) => adminService.updateUser(id, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'users'] }),
  });
}

export function useDeactivateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => adminService.deactivateUser(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'users'] }),
  });
}

export function useSendInvite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { email: string; role: UserRole; tenant_id?: string; roles?: UserRole[] }) => adminService.sendInvite(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'invites'] }),
  });
}

export function useInvites() {
  return useQuery({ queryKey: ['admin', 'invites'], queryFn: () => adminService.getInvites() });
}

// ── Tenants ────────────────────────────────────────────

export function useTenants() {
  return useQuery({ queryKey: ['admin', 'tenants'], queryFn: () => adminService.getTenants() });
}

export function useTenant(id: string) {
  return useQuery({ queryKey: ['admin', 'tenants', id], queryFn: () => adminService.getTenant(id), enabled: !!id });
}

export function useUpdateTenant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Tenant> }) => adminService.updateTenant(id, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'tenants'] }),
  });
}

export function useCreateTenant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<Tenant>) => adminService.createTenant(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'tenants'] }),
  });
}

// ── Email Config ───────────────────────────────────────

export function useEmailConfig() {
  return useQuery({ queryKey: ['admin', 'email-config'], queryFn: () => adminService.getEmailConfig() });
}

export function useUpdateEmailConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<EmailConfig>) => adminService.updateEmailConfig(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'email-config'] }),
  });
}

export function useSendTestEmail() {
  return useMutation({ mutationFn: () => adminService.sendTestEmail() });
}

export function useEmailLog() {
  return useQuery({ queryKey: ['admin', 'email-log'], queryFn: () => adminService.getEmailLog() });
}

// ── VAPI Config ────────────────────────────────────────

export function useVapiConfig() {
  return useQuery({ queryKey: ['admin', 'vapi-config'], queryFn: () => adminService.getVapiConfig() });
}

export function useUpdateVapiConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<VapiConfig>) => adminService.updateVapiConfig(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'vapi-config'] }),
  });
}

export function useTriggerTestCall() {
  return useMutation({ mutationFn: () => adminService.triggerTestCall() });
}

export function useVapiCalls() {
  return useQuery({ queryKey: ['admin', 'vapi-calls'], queryFn: () => adminService.getVapiCalls() });
}

// ── Integrations ───────────────────────────────────────

export function useIntegrations() {
  return useQuery({ queryKey: ['admin', 'integrations'], queryFn: () => adminService.getIntegrations() });
}

export function useTestIntegration() {
  return useMutation({ mutationFn: (provider: IntegrationProvider) => adminService.testIntegration(provider) });
}

// ── Admin Sites ────────────────────────────────────────

export function useAdminSites() {
  return useQuery({ queryKey: ['admin', 'sites'], queryFn: () => adminService.getSites() });
}

export function useCreateSite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<Site>) => adminService.createSite(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'sites'] }),
  });
}

export function useUpdateSite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Site> }) => adminService.updateSite(id, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'sites'] }),
  });
}

export function useDeleteSite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => adminService.deleteSite(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'sites'] }),
  });
}

// ── Admin Customers ────────────────────────────────────

export function useAdminCustomers() {
  return useQuery({ queryKey: ['admin', 'customers'], queryFn: () => adminService.getCustomers() });
}

export function useAdminCustomer(id: string) {
  return useQuery({ queryKey: ['admin', 'customers', id], queryFn: () => adminService.getCustomer(id), enabled: !!id });
}

export function useCreateCustomer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<Customer>) => adminService.createCustomer(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'customers'] });
      qc.invalidateQueries({ queryKey: ['customers'] });
    },
  });
}

export function useUpdateCustomer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Customer> }) => adminService.updateCustomer(id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'customers'] });
      qc.invalidateQueries({ queryKey: ['customers'] });
    },
  });
}
export function useDeleteCustomer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => adminService.deleteCustomer(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'customers'] });
      qc.invalidateQueries({ queryKey: ['customers'] });
    },
  });
}
